import { useState } from 'react';
import { payablesApi } from '../../api/services';
import type { CreatePayableInvoiceDto, PayableInvoiceDto, SaveSupplierDto, SupplierDto } from '../../api/types';
import {
  Alert, Badge, Button, Check, Field, Form, Input, Loadable, Modal, NumberInput, PageHeader, Pager, Select, Table, Tabs, Toolbar,
  fmtDate, fmtMoney, money, round2, todayIso, useAction, useAsync,
} from '../../ui/components';
import { BankAccountSelect, CurrencySelect, TemplateSelect } from '../../ui/pickers';
import { AgingView, StatementView } from '../shared/AgingViews';
import { PageHelp } from '../../ui/help';
import { useCountry } from '../../ui/company';
import { AttachmentViewer, ScanButton, ScanSummary, type ScanOutcome } from '../../ui/invoiceScan';

type Tab = 'suppliers' | 'invoices' | 'payments' | 'aging' | 'statement' | 'services';

export function PayablesPage() {
  const [tab, setTab] = useState<Tab>('invoices');
  return (
    <>
      <PageHeader title="Cuentas por pagar" />
      <PageHelp page="payables" />
      <Tabs<Tab> tabs={[{ key: 'invoices', label: 'Facturas' }, { key: 'payments', label: 'Pagos' }, { key: 'suppliers', label: 'Proveedores' }, { key: 'services', label: 'Tipos de servicio' }, { key: 'aging', label: 'Antigüedad' }, { key: 'statement', label: 'Estado de cuenta' }]} active={tab} onChange={setTab} />
      {tab === 'suppliers' && <SuppliersTab />}
      {tab === 'invoices' && <InvoicesTab />}
      {tab === 'payments' && <PaymentsTab />}
      {tab === 'services' && <ServiceTypesTab />}
      {tab === 'aging' && <AgingView load={(asOf) => payablesApi.aging(asOf)} csvReport="payables-aging" />}
      {tab === 'statement' && <StatementTab />}
    </>
  );
}

function SupplierSelect({ value, onChange, reloadKey = 0 }: { value: number | null; onChange: (v: number) => void; reloadKey?: number }) {
  const { data } = useAsync(() => payablesApi.suppliers(undefined, 1, 200), [reloadKey]);
  return <Select value={value ?? ''} onChange={onChange} placeholder="Proveedor" options={(data?.items ?? []).map((s) => ({ value: s.code, label: `${s.code} · ${s.name}` }))} />;
}

function SuppliersTab() {
  const [text, setText] = useState('');
  const [page, setPage] = useState(1);
  const list = useAsync(() => payablesApi.suppliers(text.trim() || undefined, page), [text, page]);
  const [editing, setEditing] = useState<SupplierDto | 'new' | null>(null);
  return (
    <>
      <Toolbar><Input value={text} onChange={(v) => { setText(v); setPage(1); }} placeholder="Buscar proveedor" /><Button kind="primary" onClick={() => setEditing('new')}>Nuevo proveedor</Button></Toolbar>
      <Loadable state={list}>
        {(r) => (
          <>
            <Table rows={r.items} rowKey={(s) => s.code} onRowClick={setEditing} columns={[
              { key: 'c', title: 'Código', render: (s) => s.code },
              { key: 'n', title: 'Nombre', render: (s) => s.name },
              { key: 'i', title: 'Identificación', render: (s) => s.idNumber ?? '' },
              { key: 't', title: 'Servicio', render: (s) => s.serviceTypeName ?? '' },
              money<SupplierDto>('b', 'Saldo', (s) => s.balance),
              { key: 'e', title: 'Estado', render: (s) => (s.status === 'I' ? <Badge tone="neutral">Inactivo</Badge> : s.status === 'A' || !s.status ? <Badge tone="ok">Activo</Badge> : s.status) },
            ]} />
            <Pager page={r.page} totalPages={r.totalPages} total={r.total} onPage={setPage} />
          </>
        )}
      </Loadable>
      {editing && <SupplierForm supplier={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); list.reload(); }} />}
    </>
  );
}

function SupplierForm({ supplier, initial, onClose, onSaved }: { supplier: SupplierDto | null; initial?: Partial<SaveSupplierDto>; onClose: () => void; onSaved: (saved?: SupplierDto) => void }) {
  const types = useAsync(() => payablesApi.serviceTypes(), []);
  const country = useCountry();
  const [v, setV] = useState<SaveSupplierDto>({
    name: supplier?.name ?? '', serviceTypeId: supplier?.serviceTypeId ?? 0, idNumber: supplier?.idNumber ?? '', idType: supplier?.idType ?? null, phone: supplier?.phone ?? '',
    mobile: supplier?.mobile ?? '', fax: supplier?.fax ?? '', address: supplier?.address ?? '', email: supplier?.email ?? '', chargesVat: supplier?.chargesVat ?? true,
    nationality: supplier?.nationality ?? '', d151Classification: supplier?.d151Classification ?? '', contact: supplier?.contact ?? '', status: supplier?.status === 'I' ? 'I' : 'A',
    ...initial,
  });
  const { run, busy, fieldErrors } = useAction();
  const s = (k: keyof SaveSupplierDto) => (x: string) => setV({ ...v, [k]: x });
  return (
    <Modal title={supplier ? `Proveedor ${supplier.code}` : 'Nuevo proveedor'} onClose={onClose} size="lg">
      <Form onSubmit={() => run(async () => { const saved = supplier ? await payablesApi.updateSupplier(supplier.code, v) : await payablesApi.createSupplier(v); onSaved(saved); }, { success: 'Proveedor guardado.' })}>
        <div className="acc-grid-form">
          <Field label="Nombre" error={fieldErrors.Name}><Input value={v.name} onChange={s('name')} required maxLength={100} /></Field>
          <Field label="Tipo de servicio" error={fieldErrors.ServiceTypeId}><Select value={v.serviceTypeId || ''} onChange={(x) => setV({ ...v, serviceTypeId: x })} placeholder="Seleccione" options={(types.data ?? []).map((t) => ({ value: t.id, label: t.description }))} /></Field>
          <Field label="Identificación" error={fieldErrors.IdNumber}><Input value={v.idNumber ?? ''} onChange={s('idNumber')} maxLength={50} /></Field>
          <Field label="Teléfono"><Input value={v.phone ?? ''} onChange={s('phone')} maxLength={50} /></Field>
          <Field label="Celular"><Input value={v.mobile ?? ''} onChange={s('mobile')} maxLength={50} /></Field>
          <Field label="Correo" error={fieldErrors.Email}><Input type="email" value={v.email ?? ''} onChange={s('email')} maxLength={50} /></Field>
          <Field label="Contacto"><Input value={v.contact ?? ''} onChange={s('contact')} maxLength={100} /></Field>
          {country?.code === 'CR' && <Field label="Clasificación para la declaración D-151" hint="Sólo Costa Rica"><Input value={v.d151Classification ?? ''} onChange={s('d151Classification')} maxLength={50} /></Field>}
          <Field label="Estado" error={fieldErrors.Status}><Select value={v.status ?? 'A'} onChange={(x) => setV({ ...v, status: x })} options={[{ value: 'A', label: 'Activo' }, { value: 'I', label: 'Inactivo' }]} /></Field>
        </div>
        <Field label="Dirección" wide error={fieldErrors.Address}><Input value={v.address ?? ''} onChange={s('address')} maxLength={200} /></Field>
        <Check checked={v.chargesVat} onChange={(x) => setV({ ...v, chargesVat: x })} label={`Cobra ${country?.salesTax ?? 'impuesto de ventas'}`} />
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy}>Guardar</Button></div>
      </Form>
    </Modal>
  );
}

function InvoicesTab() {
  const [f, setF] = useState({ supplierCode: null as number | null, onlyPending: true, invoiceNumber: '', page: 1 });
  const list = useAsync(() => payablesApi.invoices({ supplierCode: f.supplierCode ?? undefined, onlyPending: f.onlyPending, invoiceNumber: f.invoiceNumber || undefined, page: f.page, pageSize: 50 }), [f]);
  const [creating, setCreating] = useState(false);
  const [photo, setPhoto] = useState<PayableInvoiceDto | null>(null);
  const { run, busy } = useAction();
  return (
    <>
      <Toolbar>
        <SupplierSelect value={f.supplierCode} onChange={(v) => setF({ ...f, supplierCode: v || null, page: 1 })} />
        <Input value={f.invoiceNumber} onChange={(v) => setF({ ...f, invoiceNumber: v, page: 1 })} placeholder="Número de factura" />
        <Check checked={f.onlyPending} onChange={(v) => setF({ ...f, onlyPending: v, page: 1 })} label="Sólo pendientes" />
        <Button kind="primary" onClick={() => setCreating(true)}>Registrar factura</Button>
      </Toolbar>
      <Loadable state={list}>
        {(r) => (
          <>
            <Table rows={r.items} rowKey={(i) => i.entryCode} columns={[
              { key: 'a', title: 'Asiento', render: (i) => i.entryCode },
              { key: 'p', title: 'Proveedor', render: (i) => i.supplierName ?? i.supplierCode },
              { key: 'n', title: 'Factura', render: (i) => i.invoiceNumber },
              { key: 'f', title: 'Fecha', render: (i) => fmtDate(i.date) },
              { key: 'm', title: 'Moneda', render: (i) => i.originalCurrency },
              money<PayableInvoiceDto>('t', 'Total', (i) => i.total),
              money<PayableInvoiceDto>('s', 'Saldo', (i) => i.balance),
              { key: 'e', title: 'Estado', render: (i) => <Badge tone={i.status === 'Pagado' ? 'ok' : i.status === 'Anulado' ? 'bad' : i.daysOverdue > 0 ? 'bad' : 'warn'}>{i.status}</Badge> },
              { key: 'ph', title: 'Foto', render: (i) => (i.hasAttachment ? <Button kind="link" onClick={() => setPhoto(i)}>Ver factura</Button> : null) },
              { key: 'x', title: '', render: (i) => (i.status === 'Pendiente' && i.paid === 0 ? <Button kind="link" busy={busy} onClick={() => run((reason) => payablesApi.cancelInvoice(i.entryCode, reason), { confirm: `Anular la factura ${i.invoiceNumber}? Se anula su asiento con reverso.`, danger: true, requireReason: true, success: 'Factura anulada.' }).then(list.reload)}>Anular</Button> : null) },
            ]} />
            <Pager page={r.page} totalPages={r.totalPages} total={r.total} onPage={(p) => setF({ ...f, page: p })} />
          </>
        )}
      </Loadable>
      {creating && <InvoiceForm onClose={() => setCreating(false)} onSaved={() => { setCreating(false); list.reload(); }} />}
      {photo && <AttachmentViewer origin="payables" reference={photo.entryCode} title={`Factura ${photo.invoiceNumber} · ${photo.supplierName ?? photo.supplierCode}`} onClose={() => setPhoto(null)} />}
    </>
  );
}

function InvoiceForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [v, setV] = useState<CreatePayableInvoiceDto>({ supplierCode: 0, invoiceNumber: '', invoiceDate: todayIso(), dueDate: null, currencyId: 0, exchangeRate: null, subtotal: 0, tax: 0, exempt: 0, taxed: 0, parafiscalContribution: 0, total: 0, taxPercent: 13, isExpense: true, d151Classification: null, description: '', templateCode: null });
  const [dups, setDups] = useState<string[]>([]);
  const { run, busy, fieldErrors } = useAction();
  const [scan, setScan] = useState<ScanOutcome | null>(null);
  const [newSupplier, setNewSupplier] = useState<Partial<SaveSupplierDto> | null>(null);
  const [suppliersKey, setSuppliersKey] = useState(0);
  const computed = round2(v.subtotal + v.tax + v.parafiscalContribution);
  /** Precarga lo que leyó la IA. Lo que no se pudo leer conserva el valor actual del formulario. */
  const applyScan = (o: ScanOutcome) => {
    const r = o.result;
    const tax = r.taxAmount ?? 0;
    const total = r.total ?? v.total;
    const subtotal = r.subtotal ?? (r.total != null ? round2(r.total - tax - (r.otherCharges ?? 0)) : v.subtotal);
    const next: CreatePayableInvoiceDto = {
      ...v,
      supplierCode: r.supplierCode ?? v.supplierCode,
      invoiceNumber: r.invoiceNumber ?? v.invoiceNumber,
      invoiceDate: r.invoiceDate ? r.invoiceDate.slice(0, 10) : v.invoiceDate,
      dueDate: r.dueDate ? r.dueDate.slice(0, 10) : v.dueDate,
      currencyId: r.currencyId ?? v.currencyId,
      subtotal, tax, total,
      taxPercent: r.taxRatePercent != null ? Math.round(r.taxRatePercent) : v.taxPercent,
      exempt: r.exemptAmount ?? 0,
      taxed: r.taxableAmount ?? (tax > 0 ? subtotal - (r.exemptAmount ?? 0) : 0),
      description: r.description ?? v.description,
      templateCode: r.suggestedTemplateCode ?? v.templateCode,
      attachmentId: r.attachmentId,
    };
    setV(next);
    setScan(o);
    setDups([]);
    if (next.supplierCode && next.invoiceNumber.trim())
      payablesApi.duplicates(next.supplierCode, [next.invoiceNumber.trim()]).then(setDups).catch(() => undefined);
  };
  const checkDup = async () => {
    if (v.supplierCode && v.invoiceNumber.trim()) setDups(await payablesApi.duplicates(v.supplierCode, [v.invoiceNumber.trim()]).catch(() => []));
  };
  return (
    <Modal title="Registrar factura de proveedor" onClose={onClose} size="lg">
      <Form onSubmit={() => run(async () => { await payablesApi.createInvoice({ ...v, invoiceNumber: v.invoiceNumber.trim(), description: v.description?.trim() || null }); onSaved(); }, { success: 'Factura registrada con su asiento.' })}>
        {!scan && <div className="acc-toolbar"><ScanButton origin="payables" onResult={applyScan} /></div>}
        {scan && <ScanSummary outcome={scan} onDiscard={() => { setScan(null); setV({ ...v, attachmentId: null }); }} />}
        {scan && !v.supplierCode && scan.result.supplierName && (
          <Alert kind="warn">
            El proveedor <strong>{scan.result.supplierName}</strong>{scan.result.supplierTaxId ? ` (${scan.result.supplierTaxId})` : ''} no está registrado.{' '}
            <Button kind="link" onClick={() => setNewSupplier({ name: scan.result.supplierName?.slice(0, 100) ?? '', idNumber: scan.result.supplierTaxId?.slice(0, 50) ?? '' })}>Crear proveedor</Button>
          </Alert>
        )}
        {scan && (scan.result.otherCharges ?? 0) > 0 && (
          <Alert kind="warn">La factura trae otros cargos por {fmtMoney(scan.result.otherCharges)}. Indique en qué campo van (por ejemplo contribución parafiscal) o súmelos al subtotal, para que el total cuadre.</Alert>
        )}
        <div className="acc-grid-form">
          <Field label="Proveedor"><SupplierSelect value={v.supplierCode || null} reloadKey={suppliersKey} onChange={(x) => setV({ ...v, supplierCode: x })} /></Field>
          <Field label="Número de factura" error={fieldErrors.InvoiceNumber}><input className="acc-input" value={v.invoiceNumber} onChange={(e) => setV({ ...v, invoiceNumber: e.target.value })} onBlur={checkDup} required maxLength={500} /></Field>
          <Field label="Fecha"><Input type="date" value={v.invoiceDate} onChange={(x) => setV({ ...v, invoiceDate: x })} required /></Field>
          <Field label="Vencimiento" hint="Se guarda sólo si la función de vencimientos está habilitada"><Input type="date" value={v.dueDate ?? ''} onChange={(x) => setV({ ...v, dueDate: x || null })} /></Field>
          <Field label="Moneda"><CurrencySelect value={v.currencyId || null} onChange={(x) => setV({ ...v, currencyId: x })} /></Field>
          <Field label="Tipo de cambio"><NumberInput step="0.00001" value={v.exchangeRate} onChange={(x) => setV({ ...v, exchangeRate: x || null })} /></Field>
          <Field label="Subtotal"><NumberInput value={v.subtotal} onChange={(x) => setV({ ...v, subtotal: x })} min={0} /></Field>
          <Field label="Impuesto"><NumberInput value={v.tax} onChange={(x) => setV({ ...v, tax: x })} min={0} /></Field>
          <Field label="% impuesto"><NumberInput step="1" value={v.taxPercent} onChange={(x) => setV({ ...v, taxPercent: x })} min={0} max={100} /></Field>
          <Field label="Exento"><NumberInput value={v.exempt} onChange={(x) => setV({ ...v, exempt: x })} min={0} /></Field>
          <Field label="Gravado"><NumberInput value={v.taxed} onChange={(x) => setV({ ...v, taxed: x })} min={0} /></Field>
          <Field label="Contribución parafiscal"><NumberInput value={v.parafiscalContribution} onChange={(x) => setV({ ...v, parafiscalContribution: x })} min={0} /></Field>
          <Field label="Total" hint={`Subtotal + impuesto + contribución = ${fmtMoney(computed)}`}><NumberInput value={v.total} onChange={(x) => setV({ ...v, total: x })} min={0} /></Field>
          <Field label="Plantilla"><TemplateSelect moduleCode="CxP" value={v.templateCode} onChange={(x) => setV({ ...v, templateCode: x })} /></Field>
        </div>
        <Field label="Descripción" wide><Input value={v.description ?? ''} onChange={(x) => setV({ ...v, description: x })} maxLength={200} /></Field>
        <Check checked={v.isExpense} onChange={(x) => setV({ ...v, isExpense: x })} label="Es gasto" />
        {dups.length > 0 && <Alert kind="error">Ya existe una factura con ese número para el proveedor: {dups.join(', ')}.</Alert>}
        {v.total > 0 && round2(v.total) !== computed && <Alert kind="warn">El total no coincide con subtotal + impuesto + contribución.</Alert>}
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy} disabled={!v.supplierCode || v.total <= 0 || !v.currencyId || dups.length > 0}>Registrar</Button></div>
      </Form>
      {newSupplier && (
        <SupplierForm supplier={null} initial={newSupplier} onClose={() => setNewSupplier(null)}
          onSaved={(saved) => { setNewSupplier(null); setSuppliersKey((k) => k + 1); if (saved) setV((cur) => ({ ...cur, supplierCode: saved.code })); }} />
      )}
    </Modal>
  );
}

function PaymentsTab() {
  const [supplierCode, setSupplierCode] = useState<number | null>(null);
  const list = useAsync(() => payablesApi.payments(supplierCode ?? undefined), [supplierCode]);
  const [creating, setCreating] = useState(false);
  const { run, busy } = useAction();
  return (
    <>
      <Toolbar><SupplierSelect value={supplierCode} onChange={(v) => setSupplierCode(v || null)} /><Button kind="primary" onClick={() => setCreating(true)}>Registrar pago</Button></Toolbar>
      <Loadable state={list}>
        {(rows) => (
          <Table rows={rows} rowKey={(p) => p.paymentEntryCode} columns={[
            { key: 'a', title: 'Asiento', render: (p) => p.paymentEntryCode },
            { key: 'n', title: 'Pago', render: (p) => p.paymentNumber },
            { key: 'f', title: 'Fecha', render: (p) => fmtDate(p.date) },
            { key: 't', title: 'Tipo', render: (p) => p.paymentType ?? '' },
            { key: 'r', title: 'Referencia', render: (p) => p.reference },
            { key: 'm', title: 'Monto', align: 'right', render: (p) => fmtMoney(p.amount) },
            { key: 'fa', title: 'Facturas', render: (p) => p.lines.map((l) => l.invoiceNumber ?? l.invoiceEntryCode).join(', ') },
            { key: 'e', title: 'Estado', render: (p) => p.status },
            { key: 'x', title: '', render: (p) => (p.status !== 'Anulado' ? <Button kind="link" busy={busy} onClick={() => run((reason) => payablesApi.cancelPayment(p.paymentEntryCode, reason), { confirm: 'Anular el pago? Las facturas vuelven a quedar pendientes.', danger: true, requireReason: true, success: 'Pago anulado.' }).then(list.reload)}>Anular</Button> : null) },
          ]} />
        )}
      </Loadable>
      {creating && <PaymentForm onClose={() => setCreating(false)} onSaved={() => { setCreating(false); list.reload(); }} />}
    </>
  );
}

function PaymentForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const types = useAsync(() => payablesApi.paymentTypes(), []);
  const [h, setH] = useState({ supplierCode: 0, paymentDate: todayIso(), paymentTypeId: '', reference: '', observation: '', exchangeRate: null as number | null, bankAccountCode: '', templateCode: null as number | null });
  const invoices = useAsync(() => (h.supplierCode ? payablesApi.invoices({ supplierCode: h.supplierCode, onlyPending: true, pageSize: 200 }) : Promise.resolve(null)), [h.supplierCode]);
  const [alloc, setAlloc] = useState<Record<string, number>>({});
  const { run, busy } = useAction();
  const total = round2(Object.values(alloc).reduce((s, x) => s + (x || 0), 0));
  const currencies = new Set((invoices.data?.items ?? []).filter((i) => alloc[i.entryCode] > 0).map((i) => i.originalCurrency));
  return (
    <Modal title="Registrar pago a proveedor" onClose={onClose} size="lg">
      <Form onSubmit={() => run(async () => {
        await payablesApi.createPayment({ ...h, reference: h.reference.trim(), observation: h.observation.trim() || null, bankAccountCode: h.bankAccountCode || null,
          allocations: Object.entries(alloc).filter(([, a]) => a > 0).map(([c, a]) => ({ invoiceEntryCode: c, amount: round2(a) })) });
        onSaved();
      }, { success: 'Pago registrado.' })}>
        <div className="acc-grid-form">
          <Field label="Proveedor"><SupplierSelect value={h.supplierCode || null} onChange={(x) => { setH({ ...h, supplierCode: x }); setAlloc({}); }} /></Field>
          <Field label="Fecha"><Input type="date" value={h.paymentDate} onChange={(x) => setH({ ...h, paymentDate: x })} /></Field>
          <Field label="Tipo de pago"><Select value={h.paymentTypeId || ''} onChange={(x) => setH({ ...h, paymentTypeId: x })} placeholder="Seleccione" options={(types.data ?? []).map((t) => ({ value: t.id, label: t.description }))} /></Field>
          <Field label="Referencia"><Input value={h.reference} onChange={(x) => setH({ ...h, reference: x })} required maxLength={50} /></Field>
          <Field label="Cuenta bancaria" hint="Su cuenta contable reemplaza la línea BANCO de la plantilla"><BankAccountSelect value={h.bankAccountCode} onChange={(x) => setH({ ...h, bankAccountCode: x })} placeholder="(usar la cuenta de la plantilla)" /></Field>
          <Field label="Tipo de cambio"><NumberInput step="0.00001" value={h.exchangeRate} onChange={(x) => setH({ ...h, exchangeRate: x || null })} /></Field>
          <Field label="Plantilla"><TemplateSelect moduleCode="CxP" value={h.templateCode} onChange={(x) => setH({ ...h, templateCode: x })} /></Field>
        </div>
        <Field label="Observación" wide><Input value={h.observation} onChange={(x) => setH({ ...h, observation: x })} maxLength={200} /></Field>
        {h.supplierCode > 0 && (
          <Loadable state={invoices}>
            {(r) => (
              <Table rows={r?.items ?? []} rowKey={(i) => i.entryCode} empty="El proveedor no tiene facturas pendientes." columns={[
                { key: 'n', title: 'Factura', render: (i) => i.invoiceNumber },
                { key: 'f', title: 'Fecha', render: (i) => fmtDate(i.date) },
                { key: 'm', title: 'Moneda', render: (i) => i.originalCurrency },
                money<PayableInvoiceDto>('s', 'Saldo', (i) => i.balance),
                { key: 'a', title: 'Pagar', render: (i) => <NumberInput value={alloc[i.entryCode] ?? null} min={0} max={i.balance} onChange={(x) => setAlloc({ ...alloc, [i.entryCode]: Math.min(x, i.balance) })} /> },
                { key: 'x', title: '', render: (i) => <Button kind="link" onClick={() => setAlloc({ ...alloc, [i.entryCode]: i.balance })}>Todo</Button> },
              ]} />
            )}
          </Loadable>
        )}
        {currencies.size > 1 && <Alert kind="error">Todas las facturas de un pago deben estar en la misma moneda.</Alert>}
        <div className="acc-actions"><strong>Total: {fmtMoney(total)}</strong><Button onClick={onClose}>Cancelar</Button>
          <Button kind="primary" type="submit" busy={busy} disabled={total <= 0 || !h.paymentTypeId || !h.reference.trim() || currencies.size > 1}>Registrar pago</Button></div>
      </Form>
    </Modal>
  );
}

function ServiceTypesTab() {
  const list = useAsync(() => payablesApi.serviceTypes(), []);
  const [text, setText] = useState('');
  const { run, busy } = useAction();
  return (
    <>
      <Form onSubmit={() => run(() => payablesApi.createServiceType(text.trim()), { success: 'Tipo creado.' }).then(() => { setText(''); list.reload(); })} className="acc-inline-form">
        <Field label="Nuevo tipo de servicio"><Input value={text} onChange={setText} required maxLength={100} /></Field>
        <Button type="submit" busy={busy}>Agregar</Button>
      </Form>
      <Loadable state={list}>
        {(rows) => (
          <Table rows={rows} rowKey={(t) => t.id} columns={[
            { key: 'i', title: 'ID', render: (t) => t.id },
            { key: 'd', title: 'Descripción', render: (t) => t.description },
            { key: 'n', title: 'Proveedores', align: 'right', render: (t) => t.supplierCount },
            { key: 'x', title: '', render: (t) => (t.supplierCount === 0 ? <Button kind="link" busy={busy} onClick={() => run(() => payablesApi.deleteServiceType(t.id), { confirm: 'Eliminar el tipo de servicio?', danger: true, success: 'Eliminado.' }).then(list.reload)}>Eliminar</Button> : null) },
          ]} />
        )}
      </Loadable>
    </>
  );
}

function StatementTab() {
  const [supplier, setSupplier] = useState<number | null>(null);
  return (
    <>
      <Toolbar><SupplierSelect value={supplier} onChange={(v) => setSupplier(v || null)} /></Toolbar>
      {supplier && <StatementView load={(from, to) => payablesApi.statement(supplier, from, to)} />}
    </>
  );
}
